import React, { useEffect, useState } from 'react';
import { uppercase } from '../services/utils/format'
import { useRegistrationContext } from '../contexts/registrationContext'
import { useForm } from '../hooks/useForm'
import { strings } from "../assets/resources/translations"
import Select from '../elements/Select'
import Input from '../elements/Input'
import Ancillary from './Ancillary';

const createGenderOptions = () => {
	return [
		{
			name: strings.female,
			key: "female"
		},
		{
			name: strings.male,
			key: "male"
		}
	]
}

const createFlightMealOptions = () => {
	return [
		{
			name: strings.standard,
			key: "standard"
		},
		{
			name: strings.vegan,
			key: "vegan"
		},
		{
			name: strings.laktosfree,
			key: "laktosfree"
		},
		{
			name: strings.glutenfree,
			key: "glutenfree"
		},
		{
			name: strings.laktoveg,
			key: "laktoveg"
		},
		{
			name: strings.diabetes,
			key: "diabetes"
		}
	]
}

const createCountryOptions = () => {
	return [
		{
			name: strings.sweden,
			key: 'sweden'
		},
		{
			name: strings.denmark,
			key: 'denmark'
		},
		{
			name: strings.norway,
			key: 'norway'
		},
		{
			name: strings.finland,
			key: 'finland'
		}
	]
}

// TODO: add rules
const getInit = (getContactInfo, hasFlightMeal, ancillaries, staticEmail) => {
	let inputs = {
		firstName: null,
		lastName: null,
		dateOfBirth: null,
		gender: ""
	}

	let rules = {
		firstName: ["required"],
		lastName: ["required"],
		dateOfBirth: ["required", "date"],
		gender: ["required"]
	}


	if (getContactInfo) {
		inputs = {
			...inputs,
			email: staticEmail || null,
			telephone: null,
			street: null,
			zip: null,
			city: null,
			country: ""
		}
		
		rules = {
			...rules,
			email: ["required", "email"],
			telephone: ["required", "telephone"],
			street: ["required"],
			zip: ["required"],
			city: ["required"],
			country: ["required"]
		}
	}
	
	if (hasFlightMeal) {
		inputs = { ...inputs, flightMeal: "" }
		rules = { ...rules, flightMeal: ["required"] }
	}

	if (ancillaries) {
		for (const ancillary of ancillaries) {
			inputs = { ...inputs, [ancillary.name]: undefined }
			rules = { ...rules, [ancillary.name]: ["required"] }
		}
	}

	return { inputs, rules }
}

const Customer = ({ name, getContactInfo, type, isDisabled, displayErrors, staticEmail }) => {
	const [flightMealOptions, setFlightMealOptions] = useState([])
	const [genderOptions, setGenderOptions] = useState([])
	const [countryOptions, setCountryOptions] = useState([])

	const { config: { hasFlightMeal, ancillaries }, wrapper: { updateInput, updateValidity } } = useRegistrationContext()

	const form = useForm(getInit(getContactInfo, hasFlightMeal, ancillaries, staticEmail))
	const {
		inputs,
		errors,
		setInit,
		handleChange,
		isValid
	} = form


	useEffect(() => {
		if (staticEmail) {
			form.update("email", staticEmail)
		}
	}, [staticEmail])

	
	useEffect(() => {
		const init = getInit(getContactInfo, hasFlightMeal, ancillaries, staticEmail)
		setInit(init)
		setFlightMealOptions(createFlightMealOptions())
		setGenderOptions(createGenderOptions())
		setCountryOptions(createCountryOptions())
	}, [])

	/* Update parent form on every child input change */
	useEffect(() => {
		updateInput(name, inputs)
	}, [inputs])


	useEffect(() => {
		updateValidity(name, isValid)
	}, [isValid])

	// TODO: call populate options as states

	return (
		<div className={`form-section ${isDisabled && 'disabled'}`}>
			{type === 'lead' ?
				<h2 className="text lead large">{uppercase(strings.leadPassanger)}</h2>
				: <h2 className="text lead large">{uppercase(strings.participant)}</h2>
			}
			<span className="section-grid">
				<Input
					name="firstName"
					label={uppercase(strings.firstName)}
					placeholder={strings.firstNamePlaceholder}
					value={inputs.firstName}
					error={errors.firstName}
					required={true}
					maxLength="40"
					onChange={handleChange}
					disabled={isDisabled}
					displayError={displayErrors}

				/>
				<Input
					name="lastName"
					label={uppercase(strings.lastName)}
					placeholder={strings.lastNamePlaceholder}
					value={inputs.lastName}
					error={errors.lastName}
					required={true}
					maxLength="40"
					onChange={handleChange}
					disabled={isDisabled}
					displayError={displayErrors}
				/>
				<Input
					name="dateOfBirth"
					label={uppercase(strings.dateOfBirth)}
					placeholder={strings.dateOfBirthPlaceholder}
					value={inputs.dateOfBirth}
					error={errors.dateOfBirth}
					required={true}
					format={/[0-9]{2}-{1}[0-9]{2}/}
					onChange={handleChange}
					disabled={isDisabled}
					displayError={displayErrors}
				/>
				<Select
					name="gender"
					label={uppercase(strings.gender)}
					placeholder={strings.genderPlaceholder}
					value={inputs.gender}
					error={errors.gender}
					options={genderOptions}
					onChange={handleChange}
					disabled={isDisabled}
					displayError={displayErrors}
				/>
			</span>
			{getContactInfo &&
				<span className="section-grid">
					<Input
						name="email"
						label={uppercase(strings.email)}
						placeholder={!staticEmail ? strings.email : staticEmail	}
						value={inputs.email}
						error={errors.email}
						onChange={handleChange}
						disabled={!staticEmail ? isDisabled : true}
						displayError={displayErrors}
					/>

					<Input name="telephone"
						label={uppercase(strings.telephone)}
						placeholder={strings.telephonePlaceholder}
						value={inputs.telephone}
						error={errors.telephone}
						onChange={handleChange}
						disabled={isDisabled}
						displayError={displayErrors}
					/>

					<Input name="street"
						label={uppercase(strings.street)}
						placeholder={strings.streetPlaceholder}
						value={inputs.street}
						error={errors.street}
						onChange={handleChange}
						disabled={isDisabled}
						displayError={displayErrors}
					/>

					<Input name="zip"
						label={uppercase(strings.zip)}
						placeholder={strings.zipPlaceholder}
						value={inputs.zip}
						error={errors.zip}
						onChange={handleChange}
						disabled={isDisabled}
						displayError={displayErrors}
					/>

					<Input name="city"
						label={uppercase(strings.city)}
						placeholder={strings.cityPlaceholder}
						value={inputs.city}
						error={errors.city}
						onChange={handleChange}
						disabled={isDisabled}
						displayError={displayErrors}
					/>

					<Select name="country"
						label={uppercase(strings.country)}
						placeholder={strings.countryPlaceholder}
						options={countryOptions}
						value={inputs.country}
						error={errors.country}
						onChange={handleChange}
						disabled={isDisabled}
						displayError={displayErrors}
					/>
				</span>
			}
			{hasFlightMeal &&
				<span className="section-grid">
					<Select name="flightMeal"
						label={uppercase(strings.flightMeal)}
						placeholder={strings.flightMealPlaceholder}
						value={inputs.flightMeal}
						error={errors.flightMeal}
						options={flightMealOptions}
						onChange={handleChange}
						disabled={isDisabled}
						displayError={displayErrors}
					/>
				</span>
			}
			<span className="section-grid ">
				{ancillaries &&
					ancillaries.map((ancillary, index) => <span className="grid-first-column-only" key={index}><Ancillary props={ancillary} value={inputs[ancillary.name]} errors={errors} handleChange={handleChange} disabled={isDisabled} displayError={displayErrors} /></span>)}
			</span>

		</div>
	)
}

export default Customer